/**
 * RecursiveFormRenderer — F12.N Etap 1, task 5a.
 *
 * Uniwersalny rekurencyjny renderer formularzy modułów systemowych.
 * Rozumie NOWY rekurencyjny kształt `layout_config` (SellCall-style, ADR-0028):
 * elementy z dziećmi (children/tabs/accordionItems/steps) dispatchowane per
 * `element.type`.
 *
 * Używany przez 5 stron detail (Contractor/Employee/Company/User/ContractorContact —
 * podłączane w E1.T5b). Każda strona dostarcza:
 *  - `renderField(fieldId)` → renderuje pole (różne CORE_RENDERERS per moduł)
 *  - `contextRenderers` → mapa typów placeholder (related_records_table,
 *    audit_log_view, tree_picker_view, satellite_view, document_items_view)
 *
 * Dispatch wg `element.type`:
 *  - `'field'`              → `renderField(element.fieldId)`
 *  - `'section'`            → wewnętrzny <SectionRenderer> (grid kolumn + children)
 *  - `'tabs'`               → <Tabs> z filtrem visibleRoleIds + sortOrder + hidden
 *  - `'accordion'`          → <AccordionRenderer> (panele z `accordionItems[]`, filtr per-rola + sortOrder + toggle)
 *  - `'stepper' / 'wizard'` → <StepperRenderer> (top-bar z numerami/ikoną + active step content; wizard ma buttons Wstecz/Dalej)
 *  - placeholder typy       → `contextRenderers[type]` lub fallback komunikat
 *  - pozostałe              → `tryRenderLayoutElement` (heading/divider/alert/...)
 *
 * Conditional visibility: każdy element jest skrywany przed renderem przez
 * `isLayoutElementVisible` (wspólny helper z LayoutElementRegistry).
 *
 * @see docs/sellcall/01-layout-builder.md
 * @see docs/ADR/0028-* (F12.N rekurencyjny model layoutu)
 */

import { Fragment, type ReactElement, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';

import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import { buildFieldScopedCss, isFieldElementVisible } from '@/components/form/styled-field';
import type { LayoutElementStyle } from '@/components/module-builder/style-panel';
import { getAnimationClass } from '@/components/module-builder/style-panel';
import { renderIconByName } from '@/components/modules/IconPicker';
import { Tabs, type TabsItem } from '@/components/Tabs';

import '@/components/module-builder/style-panel/styleAnimations.css';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface LayoutElement {
  id: string;
  type: string;
  /** Tylko dla `type === 'field'`. */
  fieldId?: string;
  /**
   * Ukrycie elementu przez nakładkę per-tenant systemowego layoutu (`tab_overrides`).
   * Dziś ustawiane wyłącznie na polach (`type:'field'`, keyed po fieldId) przez
   * `applyTabOverrides`. Renderer karty pomija element z tą flagą.
   */
  hidden?: boolean;
  /** Szerokość pola (1|2|3) — col-span w gridzie sekcji. Parytet z kartoteką Kreatora. */
  width?: number;
  settings?: Record<string, unknown>;
  style?: Record<string, unknown>;
  conditionalField?: string | null;
  conditionalOperator?: string | null;
  conditionalValue?: unknown;
  /** Dzieci dla kontenerów (section/card/row/columns/fieldset/panel/...). */
  children?: LayoutElement[];
  /** Zakładki dla `type === 'tabs'`. */
  tabs?: TabItem[];
  /** Panele dla `type === 'accordion'`. */
  accordionItems?: AccordionItem[];
  /** Kroki dla `type === 'stepper' | 'wizard'`. */
  steps?: Step[];
}

export interface TabItem {
  id: string;
  slug: string;
  title: string;
  icon?: string;
  visibleRoleIds?: string[];
  validateOnChange?: boolean;
  blockOnError?: boolean;
  hidden?: boolean;
  sortOrder?: number;
  children: LayoutElement[];
}

export interface AccordionItem {
  id: string;
  title: string;
  defaultExpanded?: boolean;
  visibleRoleIds?: string[];
  sortOrder?: number;
  children: LayoutElement[];
}

export interface Step {
  id: string;
  title: string;
  icon?: string;
  validation?: boolean;
  /** Per-rola visibility (Etap 3 T2 — parytet z TabItem/AccordionItem). */
  visibleRoleIds?: string[];
  sortOrder?: number;
  children: LayoutElement[];
}

export interface LayoutConfig {
  elements: LayoutElement[];
}

/**
 * Renderery elementów zależnych od kontekstu (record_links_view/attachments_view/…):
 * klucz mapy = typ elementu placeholder, wartość = renderer z domknięciem strony (id,
 * companyId, handlery). Eksportowane, żeby dedykowane komponenty zakładek (np.
 * ContractorDataTab/EmployeeDataTab) mogły dostać tę samą mapę i renderować te elementy
 * żywcem, zamiast martwego placeholdera z generycznego rejestru.
 */
export type ContextRenderers = Record<string, (props: { element: LayoutElement }) => ReactNode>;

export interface RecursiveFormRendererProps {
  /** Layout_config w nowym kształcie (z bazy lub z buildery). */
  layoutConfig: LayoutConfig;
  /**
   * Dispatch dla `element.type === 'field'` — przyjmuje fieldId + opcjonalne `settings`
   * elementu layoutu (nadpisanie etykiety/wymagalności per pole), zwraca ReactNode.
   * Każda strona detail przekazuje własną funkcję bo CORE_RENDERERS różnią się per moduł.
   * Drugi arg jest opcjonalny — starsze implementacje `(fieldId) => …` zostają kompatybilne.
   */
  renderField: (fieldId: string, settings?: Record<string, unknown>) => ReactNode;
  /**
   * Dispatch dla placeholder typów (related_records_table / audit_log_view / tree_picker_view /
   * satellite_view / document_items_view). Klucz mapy to typ elementu.
   */
  contextRenderers?: ContextRenderers;
  /**
   * Override dla zawartości konkretnej zakładki — mapa slug zakładki → renderer.
   * Jeśli zakładka ma override, renderer używa go ZAMIAST schodzić rekurencyjnie
   * w children TabItem'a. Używane w F12.N E1.T5b (wąski refactor): 5 stron detail
   * dostarczają własne komponenty per zakładka (ContractorDataTab, AddressesDynamicTab itp.)
   * zamiast pozwolić rendererowi dynamicznie złożyć pola z layout_config.
   *
   * F12 PR 2 pkt 2 (ADR-0032): renderer przekazuje `ctx.element` — pierwszy
   * LayoutElement z `tab.children` (typowo `type='related_records_table'`) zawierający
   * settings.displayFields / allowAdd / allowEdit / allowDelete. Komponenty detail
   * mogą używać tego do renderowania kolumn subcollection zgodnie z konfiguracją
   * z buildera. Sygnatura backward-compatible — istniejące `() => ReactNode`
   * po prostu ignorują ctx.
   */
  tabRenderers?: Record<string, (ctx?: { element?: LayoutElement }) => ReactNode>;
  /**
   * Controlled active tab — slug aktualnej zakładki. Aplikuje się do każdego elementu
   * type='tabs' w drzewie. Dla głównego użycia (jeden top-level tabs) obsługuje
   * deep-link `?tab=foo`. Bez tego propa Tabs są niekontrolowane (state resetuje
   * się przy re-renderach rodzica). Slug spoza WIDOCZNYCH zakładek danego `tabs` (inny widok
   * formularza, stary link, zakładka ukryta/rolą) → zaznaczona zostaje pierwsza widoczna.
   */
  activeTabId?: string;
  /** Callback przy zmianie aktywnej zakładki — dostaje slug nowej zakładki. */
  onTabChange?: (tabSlug: string) => void;
  /** Wartości formularza (do oceny conditional visibility). Default `{}`. */
  formData?: Record<string, unknown>;
  /**
   * Hook dla mechanizmu 2 per-rola (visibleRoleIds per zakładka i panel akordeonu).
   * Z F12.M Etap 4. Jeśli `undefined` — visibleRoleIds zachowuje się jak whitelist (puste=widoczne, niepuste=ukryte).
   */
  myRoleId?: string | null;
  /** Czy user jest super-userem (bypassuje visibleRoleIds). Default `false`. */
  isSuperUser?: boolean;
  /** Optional className wrappera. */
  className?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Czy zakładka / panel akordeonu jest widoczny dla bieżącego usera?
 *
 *  - super-user → zawsze tak,
 *  - pusta/brak `visibleRoleIds` → wszyscy widzą,
 *  - `visibleRoleIds` niepuste → user musi mieć role z listy.
 */
function isVisibleForRole(
  visibleRoleIds: string[] | undefined,
  myRoleId: string | null | undefined,
  isSuperUser: boolean,
): boolean {
  if (isSuperUser) return true;
  if (!visibleRoleIds || visibleRoleIds.length === 0) return true;
  if (!myRoleId) return false;
  return visibleRoleIds.includes(myRoleId);
}

// „Pola dodatkowe" NIE są osobnym blokiem ani zakładką — nigdzie w aplikacji. Pole dodatkowe
// pokazuje się wyłącznie tam, gdzie admin postawił je w kreatorze układu jako zwykłe pole
// (`type: 'field'`, rysuje je `renderField`). Typy `ext_fields_view` oraz nazwane
// `<kartoteka|klasa>_ext_fields_view` (w tym `product_ext_fields_view`) są celowo POZA tą listą
// i poza rejestrem elementów — stary blok w zapisanym układzie nie ma renderera i rysuje się
// jako nic (bez żółtego „Brak renderera").
export const PLACEHOLDER_TYPES = new Set([
  'related_records_table',
  'audit_log_view',
  'tree_picker_view',
  'additional_view',
  'additional_query_view',
  'panel_view',
  'satellite_view',
  'document_items_view',
  'record_links_view',
  'attachments_view',
  // Adres macierzysty kontrahenta jako jedna formatka (Model B) — zastępuje 7 osobnych pól.
  'contractor_primary_address_view',
  'document_preview_view',
  'decree_lines_view',
  'vat_register_view',
  'process_view',
  // „Przycisk akceptacji” — dispatch przez contextRenderers (dokumenty, kontrahenci) i
  // renderProcessLayoutElement (moduły własne); w builderze zaślepka z rejestru.
  'process_action_button',
  // „Przycisk SQL” — dispatch przez contextRenderers strony rekordu (zapis + uruchomienie
  // procedury „Na przycisk”); w builderze zaślepka z rejestru.
  'sql_button',
  'company_members_view',
  // Bloki kartoteki Kontrahenci (konfigurowalne zakładki) — renderowane przez
  // contractorContextRenderers na ContractorDetail; w builderze stub z rejestru.
  // (audit_log_view/process_view już są wyżej — Historia zmian/Akceptacje reużywają ich.)
  'contractor_contacts_view',
  'contractor_addresses_view',
  'contractor_bank_accounts_view',
  'contractor_company_access_view',
  // Bloki kartoteki Pracownicy (konfigurowalne zakładki) — renderowane przez
  // employeeContextRenderers na EmployeeDetail; w builderze stub z rejestru.
  'employee_addresses_view',
  'employee_account_view',
  // Bloki kartoteki Firmy (konfigurowalne zakładki) — companyContextRenderers na CompanyDetail.
  'company_addresses_view',
  'company_bank_accounts_view',
  // „Historia zmian" PER KARTOTEKA (zamiast wspólnego audit_log_view) — dispatch per strona detalu.
  'contractor_audit_view',
  'employee_audit_view',
  'company_audit_view',
  // „Historia zmian" PER KARTOTEKA (Użytkownicy) — dispatch na UserDetail.
  'user_audit_view',
  // Blok kartoteki Kontakty kontrahentów — dispatch na ContractorContactDetail.
  'contractor_contact_audit_view',
  // Bloki-panele kartoteki Użytkownicy (konfigurowalne zakładki) — dostęp do firm + bezpieczeństwo;
  // renderowane przez UserDetail contextRenderers, w builderze stub z rejestru.
  'user_company_access_view',
  'user_security_view',
  // dokument-blokify (2026-08-23): Audit log klas dokumentów jako przenośne bloki per klasa
  // (mirror kartotek). Dispatch na stronach szczegółu poszczególnych klas.
  'purchase_invoice_audit_view',
  'sale_invoice_audit_view',
  'contract_audit_view',
  'letter_audit_view',
  'commercial_audit_view',
  'internal_audit_view',
  'specjalne_audit_view',
  // kartoteka-blokify (2026-08-27): Towary — Audit log jako przenośny blok per kartoteka
  // (mirror kontrahentów). Dispatch na ProductDetail.
  'product_audit_view',
  // Kartoteka Zadania — „Historia zmian" jako przenośny blok; dispatch na karcie zadania.
  'task_audit_view',
]);

// ─── Renderers per typ ──────────────────────────────────────────────────────

function SectionRenderer({
  element,
  props,
}: {
  element: LayoutElement;
  props: RecursiveFormRendererProps;
}): ReactElement {
  const settings = (element.settings ?? {}) as {
    title?: string;
    columns?: number;
    showHeader?: boolean;
    showBorder?: boolean;
    collapsible?: boolean;
    defaultExpanded?: boolean;
  };
  const columns = Math.max(1, Math.min(4, Number(settings.columns ?? 2)));
  const gridClass =
    columns === 1
      ? 'grid-cols-1'
      : columns === 2
        ? 'grid-cols-1 md:grid-cols-2'
        : columns === 3
          ? 'grid-cols-1 md:grid-cols-3'
          : 'grid-cols-1 md:grid-cols-4';

  // Styl sekcji z buildera — domyślne wartości = dotychczasowe zachowanie (brak regresji).
  const showHeader = settings.showHeader !== false;
  const showBorder = settings.showBorder !== false;
  const collapsible = settings.collapsible === true;
  const [open, setOpen] = useState<boolean>(settings.defaultExpanded !== false);

  const sectionClass = showBorder
    ? 'rounded-lg border border-border-primary bg-bg-primary p-4'
    : 'p-0';
  const hasHeader = showHeader && !!settings.title;
  const bodyOpen = collapsible ? open : true;

  return (
    <section className={sectionClass} data-testid={`section-${element.id}`}>
      {hasHeader &&
        (collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="mb-3 flex w-full items-center justify-between text-left text-sm font-semibold text-text-primary"
            data-testid={`section-toggle-${element.id}`}
          >
            <span>{settings.title}</span>
            <ChevronDown
              className={`h-4 w-4 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        ) : (
          <h3 className="mb-3 text-sm font-semibold text-text-primary">{settings.title}</h3>
        ))}
      {bodyOpen && (
        <div className={`grid gap-3 ${gridClass}`}>
          {(element.children ?? []).map((child) => (
            <Fragment key={child.id}>{renderElement(child, props)}</Fragment>
          ))}
        </div>
      )}
    </section>
  );
}

function TabsRenderer({
  element,
  props,
}: {
  element: LayoutElement;
  props: RecursiveFormRendererProps;
}): ReactElement | null {
  const myRoleId = props.myRoleId ?? null;
  const isSuperUser = props.isSuperUser ?? false;

  const tabs = (element.tabs ?? [])
    .filter((tab) => !tab.hidden)
    .filter((tab) => isVisibleForRole(tab.visibleRoleIds, myRoleId, isSuperUser))
    .slice()
    .sort((a, b) => {
      const aOrd = a.sortOrder ?? Number.POSITIVE_INFINITY;
      const bOrd = b.sortOrder ?? Number.POSITIVE_INFINITY;
      return aOrd - bOrd;
    });

  if (tabs.length === 0) return null;

  // Zakładka aktywna z zewnątrz (`?tab=…`) może wskazywać slug, którego nie ma wśród WIDOCZNYCH
  // zakładek tego układu (zmiana widoku formularza na taki bez tej zakładki, stary link, zakładka
  // ukryta albo niewidoczna dla roli). `Tabs` nie znalazłby wtedy panelu: pusty formularz i żadna
  // zakładka nie zaznaczona. Spadamy na pierwszą widoczną; brak propa = `Tabs` niekontrolowane.
  const firstVisibleSlug = tabs[0]?.slug;
  const activeValue =
    props.activeTabId !== undefined && !tabs.some((tab) => tab.slug === props.activeTabId)
      ? firstVisibleSlug
      : props.activeTabId;

  const items: TabsItem[] = tabs.map((tab) => {
    const override = props.tabRenderers?.[tab.slug];
    // F12 PR 2 pkt 2: znajdź pierwszy element typu 'related_records_table' w children
    // tabu — to subcollection config edytowany w builderze (SubcollectionLayoutEditor).
    // Override consumers (np. ContactsTab) używają go do renderowania kolumn z displayFields.
    const subcollectionElement = tab.children.find((c) => c.type === 'related_records_table');
    return {
      value: tab.slug,
      label: tab.icon ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex h-4 w-4 items-center justify-center">
            {renderIconByName(tab.icon, 'h-4 w-4')}
          </span>
          <span>{tab.title}</span>
        </span>
      ) : (
        tab.title
      ),
      content: (
        <div data-testid={`tab-content-${tab.slug}`}>
          {override
            ? override({ element: subcollectionElement })
            : tab.children.map((child) => (
                <Fragment key={child.id}>{renderElement(child, props)}</Fragment>
              ))}
        </div>
      ),
    };
  });

  return (
    <Tabs
      items={items}
      idPrefix={`tabs-${element.id}`}
      value={activeValue}
      onChange={props.onTabChange}
    />
  );
}

function AccordionRenderer({
  element,
  props,
}: {
  element: LayoutElement;
  props: RecursiveFormRendererProps;
}): ReactElement | null {
  const myRoleId = props.myRoleId ?? null;
  const isSuperUser = props.isSuperUser ?? false;
  const allowMultiple =
    ((element.settings ?? {}) as { allowMultiple?: boolean }).allowMultiple ?? false;

  const items = (element.accordionItems ?? [])
    .filter((item) => isVisibleForRole(item.visibleRoleIds, myRoleId, isSuperUser))
    .slice()
    .sort((a, b) => {
      const aOrd = a.sortOrder ?? Number.POSITIVE_INFINITY;
      const bOrd = b.sortOrder ?? Number.POSITIVE_INFINITY;
      return aOrd - bOrd;
    });

  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(items.filter((i) => i.defaultExpanded).map((i) => i.id)),
  );

  if (items.length === 0) return null;

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (!allowMultiple) next.clear();
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className="divide-y divide-border-primary rounded-lg border border-border-primary"
      data-testid={`accordion-${element.id}`}
    >
      {items.map((item) => {
        const isOpen = expanded.has(item.id);
        return (
          <div key={item.id}>
            <button
              type="button"
              onClick={() => toggle(item.id)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-text-primary hover:bg-bg-secondary"
            >
              <span>{item.title}</span>
              <ChevronDown
                className={`h-4 w-4 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            {isOpen && (
              <div
                className="border-t border-border-primary p-3"
                data-testid={`accordion-panel-${item.id}`}
              >
                {item.children.length > 0 ? (
                  <div className="space-y-3">
                    {item.children.map((child) => (
                      <Fragment key={child.id}>{renderElement(child, props)}</Fragment>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PlaceholderBox({ message }: { message: string }): ReactElement {
  return (
    <div className="rounded border border-dashed border-border-primary p-4 text-xs text-text-tertiary">
      {message}
    </div>
  );
}

/**
 * StepperRenderer — runtime renderer dla `type === 'stepper' | 'wizard'`.
 * Etap 3 T2 (2026-05-17).
 *
 * Wspólny komponent dla obu typów — różni się tryb nawigacji:
 *  - **stepper**: top-bar klikalny (admin może skoczyć dowolnie po krokach), brak walidacji
 *  - **wizard**: top-bar tylko info, nawigacja przez przyciski „Dalej" / „Wstecz" (linearnie)
 *
 * Filtr per-rola + sortOrder analogiczny do AccordionRenderer. Stan
 * `activeStepIndex` lokalny (default 0).
 *
 * Walidacja required pól wizarda przed `Dalej` — odłożona (E3.T3 plan, wymaga
 * dostępu do schemy pól + formData; dziś prosty mechanizm bez wymuszenia).
 */
function StepperRenderer({
  element,
  mode,
  props,
}: {
  element: LayoutElement;
  mode: 'stepper' | 'wizard';
  props: RecursiveFormRendererProps;
}): ReactElement | null {
  const { t } = useTranslation();
  const myRoleId = props.myRoleId ?? null;
  const isSuperUser = props.isSuperUser ?? false;

  const steps = (element.steps ?? [])
    .filter((step) => isVisibleForRole(step.visibleRoleIds, myRoleId, isSuperUser))
    .slice()
    .sort((a, b) => {
      const aOrd = a.sortOrder ?? Number.POSITIVE_INFINITY;
      const bOrd = b.sortOrder ?? Number.POSITIVE_INFINITY;
      return aOrd - bOrd;
    });

  const [activeStepIndex, setActiveStepIndex] = useState(0);

  if (steps.length === 0) return null;

  const activeStep = steps[Math.min(activeStepIndex, steps.length - 1)]!;
  const isStepper = mode === 'stepper';

  return (
    <div
      className="rounded-lg border border-border-primary p-3"
      data-testid={`${mode}-${element.id}`}
    >
      {/* Top-bar — ikona + numer + tytuł per krok */}
      <ol
        className="mb-4 flex flex-wrap items-center gap-2"
        data-testid={`${mode}-nav-${element.id}`}
      >
        {steps.map((step, idx) => {
          const isActive = idx === activeStepIndex;
          const isPast = idx < activeStepIndex;
          // Stepper: klik na step nawiguje swobodnie. Wizard: top-bar info-only (linearnie przez buttons).
          const clickable = isStepper;
          return (
            <li key={step.id} className="flex items-center gap-2">
              <button
                type="button"
                onClick={clickable ? () => setActiveStepIndex(idx) : undefined}
                disabled={!clickable}
                aria-current={isActive ? 'step' : undefined}
                className={
                  'flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition ' +
                  (isActive
                    ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/40 dark:text-accent-300'
                    : isPast
                      ? 'text-text-secondary'
                      : 'text-text-muted') +
                  (clickable ? ' hover:bg-bg-secondary' : ' cursor-default')
                }
                data-testid={`${mode}-${element.id}-step-${step.id}`}
              >
                {step.icon ? (
                  <span className="inline-flex h-4 w-4 items-center justify-center">
                    {renderIconByName(step.icon, 'h-4 w-4')}
                  </span>
                ) : null}
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-accent-100 text-[10px] dark:bg-accent-900/40">
                  {idx + 1}
                </span>
                <span>{step.title}</span>
              </button>
              {idx < steps.length - 1 && (
                <ChevronDown className="h-3 w-3 -rotate-90 text-text-muted" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>

      {/* Content aktywnego kroku */}
      <div
        className="space-y-3 border-t border-border-primary pt-3"
        data-testid={`${mode}-${element.id}-content`}
      >
        {activeStep.children.length > 0 ? (
          activeStep.children.map((child) => (
            <Fragment key={child.id}>{renderElement(child, props)}</Fragment>
          ))
        ) : (
          <p className="text-xs text-text-tertiary">(krok pusty)</p>
        )}
      </div>

      {/* Wizard: linearne buttons „Wstecz" / „Dalej" */}
      {!isStepper && (
        <div className="mt-4 flex justify-between gap-2 border-t border-border-primary pt-3">
          <button
            type="button"
            onClick={() => setActiveStepIndex((i) => Math.max(0, i - 1))}
            disabled={activeStepIndex === 0}
            className="rounded border border-border-primary px-3 py-1 text-xs font-medium text-text-primary hover:bg-bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={`wizard-${element.id}-prev`}
          >
            {t('tenant.systemModuleBuilder.stepper.wizardPrev')}
          </button>
          <button
            type="button"
            onClick={() => setActiveStepIndex((i) => Math.min(steps.length - 1, i + 1))}
            disabled={activeStepIndex >= steps.length - 1}
            className="rounded bg-accent-600 px-3 py-1 text-xs font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid={`wizard-${element.id}-next`}
          >
            {t('tenant.systemModuleBuilder.stepper.wizardNext')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Główna funkcja dispatch ────────────────────────────────────────────────

function renderElement(el: LayoutElement, props: RecursiveFormRendererProps): ReactNode {
  const formData = props.formData ?? {};
  if (!isFieldElementVisible(el, formData)) return null;
  // Ukrycie przez nakładkę per-tenant (systemowy layout) — pole oznaczone `hidden`
  // przez `applyTabOverrides` nie renderuje się na karcie (builder pokazuje je
  // wyszarzone z ikonką „pokaż", więc na canvasie zostaje widoczne).
  if (el.hidden === true) return null;

  // Field — szerokość (col-span) z `el.width` lub `settings.width`; nadpisania etykiety/
  // wymagalności lecą do renderField przez `el.settings` (parytet z kartoteką Kreatora).
  if (el.type === 'field') {
    if (!el.fieldId) return null;
    const rawWidth =
      typeof el.width === 'number'
        ? el.width
        : Number((el.settings as { width?: unknown } | undefined)?.width);
    // Renderer pola zwrócił „nic" (np. pola umowy, które na create nic nie rysują) — bez wrappera
    // i bez scoped `<style>`, żeby w gridzie sekcji nie zostawała pusta komórka.
    const rendered = props.renderField(el.fieldId, el.settings);
    if (rendered === null || rendered === undefined || rendered === false) return null;
    const widthClass = rawWidth === 3 ? 'col-span-3' : rawWidth === 2 ? 'col-span-2' : 'col-span-1';
    // Dwa cele stylu formatki (builder → settings): `labelStyle` maluje etykietę,
    // `style` maluje kontrolkę (input/select/textarea). Scoped `<style>` po klasie
    // unikalnej per element — wspólna implementacja z bespoke zakładkami (styled-field).
    const bodyStyle = (el.settings as { style?: LayoutElementStyle } | undefined)?.style;
    const animClass = getAnimationClass(bodyStyle);
    const cls = `fld-${el.id}`;
    const scoped = buildFieldScopedCss(el);
    return (
      <div
        key={el.id}
        className={animClass ? `${widthClass} ${cls} ${animClass}` : `${widthClass} ${cls}`}
      >
        {scoped ? <style>{scoped}</style> : null}
        {rendered}
      </div>
    );
  }

  // Section (kontener z gridem kolumn)
  if (el.type === 'section') {
    return <SectionRenderer key={el.id} element={el} props={props} />;
  }

  // Tabs
  if (el.type === 'tabs') {
    return <Fragment key={el.id}>{<TabsRenderer element={el} props={props} />}</Fragment>;
  }

  // Akordeon (F12.N E3.T1)
  if (el.type === 'accordion') {
    return <Fragment key={el.id}>{<AccordionRenderer element={el} props={props} />}</Fragment>;
  }

  // Stepper / Wizard (F12.N Etap 3 T2)
  if (el.type === 'stepper' || el.type === 'wizard') {
    return (
      <Fragment key={el.id}>
        <StepperRenderer element={el} mode={el.type as 'stepper' | 'wizard'} props={props} />
      </Fragment>
    );
  }

  // Placeholder typy (related_records_table / audit_log_view / tree_picker_view / satellite_view)
  if (PLACEHOLDER_TYPES.has(el.type)) {
    const renderer = props.contextRenderers?.[el.type];
    if (renderer) {
      return <Fragment key={el.id}>{renderer({ element: el })}</Fragment>;
    }
    return (
      <Fragment key={el.id}>
        <PlaceholderBox message={`Brak renderera dla ${el.type}`} />
      </Fragment>
    );
  }

  // Pozostałe (heading / divider / alert / card / row / columns / fieldset / panel / text / ...) — przez LayoutElementRegistry
  const layoutNode = tryRenderLayoutElement({
    element: {
      id: el.id,
      type: el.type,
      settings: (el.settings ?? {}) as Record<string, unknown>,
    },
    children: [],
    mode: 'form',
    formData,
  });
  return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
}

// ─── Public component ──────────────────────────────────────────────────────

export function RecursiveFormRenderer(props: RecursiveFormRendererProps): ReactElement {
  return (
    <div className={props.className} data-testid="recursive-form-renderer">
      {props.layoutConfig.elements.map((el) => (
        <Fragment key={el.id}>{renderElement(el, props)}</Fragment>
      ))}
    </div>
  );
}
